/**
 * Per-module sticky active-company filter (multi-company P2 + per-module sticky picker).
 *
 * Each S-heavy module (accounting, inventory, procurement, ...) has its own
 * persisted selection. The active value is sent to the backend via the
 * `X-Active-Company-Id` header — treated by the API as a UI list-filter hint,
 * NOT a security boundary (see shared/dependencies/auth.py).
 *
 * `null` means "All companies" — the user sees data across their entire
 * accessible_company_ids set, subject to backend per-route filtering.
 *
 * Storage: `panluma_module_company_filters` is a JSON object
 * `{ [moduleId]: companyId | null }`. Per-module so users can stay in Co X
 * while moving between e.g. accounting screens without dragging that filter
 * into unrelated modules.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { getModuleByPath } from '../config/modules.config';
import { isCompanyScopedModuleId, isCompanyScopedPath } from '../utils/companyScope';
import { useCompanyScopingSync } from '../hooks/useCompanyScoping';
import {
  CompanyScopeProvider,
  getCompanyScopeValue,
  isCompanyAccessible,
  useCompanyScope,
} from './CompanyScopeContext';

const STORAGE_KEY = 'panluma_module_company_filters';

type ModuleCompanyMap = Record<string, string | null>;

function readMap(): ModuleCompanyMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as ModuleCompanyMap;
    }
  } catch {
    // fall through
  }
  return {};
}

function writeMap(map: ModuleCompanyMap): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

/**
 * Read the active company id for a specific module from localStorage.
 * Used in non-component code (axios interceptor) — no React hooks.
 */
export function getModuleCompanyId(moduleId: string): string | null {
  const map = readMap();
  return map[moduleId] ?? null;
}

/**
 * Resolve the active company id for the current URL path. Called by the axios
 * interceptor to populate `X-Active-Company-Id` without React context.
 *
 * Participation-aware: modules that participate in the global app-bar scope
 * (ERP cluster) read the single global company scope; all other modules keep
 * their own per-module sticky filter until they opt in (chained WI-4).
 *
 * BOTH branches are bounded by the JWT's signed `accessible_company_ids`: a
 * persisted id the caller can no longer access is dropped rather than sent. The
 * backend's governed read routes fail closed on it (403) and the errored query
 * is never refetched, so a stale value would strand the user on "Failed to
 * load". A per-module filter goes just as stale as the global scope, and its
 * modules (support, …) are governed too — guard the one choke point that feeds
 * the header, not just the global path.
 */
export function getActiveCompanyHeaderValue(): string | null {
  try {
    if (isCompanyScopedPath(window.location.pathname)) {
      return getCompanyScopeValue();
    }
    const module = getModuleByPath(window.location.pathname);
    const moduleCompanyId = getModuleCompanyId(module.id);
    if (!moduleCompanyId) return null;
    return isCompanyAccessible(moduleCompanyId) ? moduleCompanyId : null;
  } catch {
    return null;
  }
}

interface ActiveCompanyContextType {
  /** Active company filter for the current URL's module (null = "All companies"). */
  activeCompanyId: string | null;
  /** Set the active company filter for the current URL's module. */
  setActiveCompanyId: (companyId: string | null) => void;
  /** Read another module's stored filter (e.g. cross-module pre-fill). */
  getCompanyIdForModule: (moduleId: string) => string | null;
  /** Set the filter for a specific module (rarely needed; prefer setActiveCompanyId). */
  setCompanyIdForModule: (moduleId: string, companyId: string | null) => void;
}

const ActiveCompanyContext = createContext<ActiveCompanyContextType | undefined>(undefined);

/**
 * Public provider. Owns the global company scope (via `CompanyScopeProvider`)
 * AND the per-module sticky map, so a single mount in the app/tests wires both
 * regimes — the app-bar `CompanyScopeSwitcher` and in-page pickers share state.
 */
export function ActiveCompanyProvider({ children }: { children: ReactNode }) {
  return (
    <CompanyScopeProvider>
      <ActiveCompanyInner>{children}</ActiveCompanyInner>
    </CompanyScopeProvider>
  );
}

function ActiveCompanyInner({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const { companyScopeId, setCompanyScope } = useCompanyScope();

  // Fetch the per-module company-scoping participation map and sync it into the
  // store the switcher / DocumentCompanyField / axios interceptor read (WI-4).
  useCompanyScopingSync();
  const currentModuleId = useMemo(
    () => getModuleByPath(location.pathname).id,
    [location.pathname],
  );
  // Participating (ERP) modules are backed by the single GLOBAL company scope
  // (app-bar CompanyScopeSwitcher). Their in-page pickers read/write that same
  // value, so both controls stay in sync. Non-participating modules keep their
  // own per-module sticky slot until they opt in (chained WI-4).
  const isParticipating = isCompanyScopedModuleId(currentModuleId);

  const [map, setMap] = useState<ModuleCompanyMap>(() => readMap());

  // Persist map to localStorage on every change.
  useEffect(() => {
    writeMap(map);
  }, [map]);

  const setCompanyIdForModule = useCallback(
    (moduleId: string, companyId: string | null) => {
      if (isCompanyScopedModuleId(moduleId)) {
        setCompanyScope(companyId);
        return;
      }
      setMap((prev) => {
        if ((prev[moduleId] ?? null) === companyId) return prev;
        return { ...prev, [moduleId]: companyId };
      });
    },
    [setCompanyScope],
  );

  const setActiveCompanyId = useCallback(
    (companyId: string | null) => setCompanyIdForModule(currentModuleId, companyId),
    [currentModuleId, setCompanyIdForModule],
  );

  const getCompanyIdForModule = useCallback(
    (moduleId: string) =>
      isCompanyScopedModuleId(moduleId) ? companyScopeId : (map[moduleId] ?? null),
    [map, companyScopeId],
  );

  const activeCompanyId = isParticipating ? companyScopeId : (map[currentModuleId] ?? null);

  // Invalidate React Query cache when the per-module slot for the current
  // (non-participating) module changes. Participating modules are invalidated
  // by CompanyScopeProvider on global-scope change — watching the per-module
  // value here avoids double-invalidation on ERP scope changes.
  const perModuleValue = map[currentModuleId] ?? null;
  const invalidationValue = isParticipating ? null : perModuleValue;
  const lastHeaderValueRef = useRef<string | null>(invalidationValue);
  useEffect(() => {
    if (lastHeaderValueRef.current === invalidationValue) return;
    lastHeaderValueRef.current = invalidationValue;
    // Active-only invalidation — refresh the mounted views in place; inactive
    // queries refetch on next mount via staleTime:0. Avoids the full-cache
    // re-render storm. Mirrors CompanyScopeProvider's participating-module path.
    queryClient.invalidateQueries({ type: 'active' });
  }, [invalidationValue, queryClient]);

  const value = useMemo(
    () => ({
      activeCompanyId,
      setActiveCompanyId,
      getCompanyIdForModule,
      setCompanyIdForModule,
    }),
    [activeCompanyId, setActiveCompanyId, getCompanyIdForModule, setCompanyIdForModule],
  );

  return (
    <ActiveCompanyContext.Provider value={value}>
      {children}
    </ActiveCompanyContext.Provider>
  );
}

export function useActiveCompany(): ActiveCompanyContextType {
  const ctx = useContext(ActiveCompanyContext);
  if (!ctx) {
    throw new Error('useActiveCompany must be used within an ActiveCompanyProvider');
  }
  return ctx;
}
